import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Btn } from "@/lib/ui";
import { ForgotPassword } from "@/components/ForgotPassword";
import { toast } from "sonner";

const HOME = { super_admin: "/superadmin", tenant_admin: "/app", manager: "/manager", publisher: "/publisher",
  operations: "/app", finance: "/app", reporting: "/app", viewer: "/app", tenant_manager: "/app" };

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav(HOME[user.role] || "/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (r.ok) { toast.success(`Welcome back, ${r.user.name}`); nav(HOME[r.user.role] || "/"); }
    else toast.error(r.error);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* left brand */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-950 p-12 lg:flex">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="relative flex items-center">
          <img src="/tracking-panel-logo.png" alt="TrackingPanel — One Platform. Complete Tracking." className="h-24 w-auto max-w-[360px] object-contain" />
        </div>
        <div className="relative">
          <h1 className="font-head text-4xl font-bold leading-tight text-white xl:text-5xl">
            One tracking ID.<br /><span className="text-emerald-400">Total control.</span>
          </h1>
          <p className="mt-5 max-w-md text-slate-400">
            Multi-tenant form tracking, lead collection and a complete mini affiliate network — publishers, offers, payouts, settlements and profit & loss, all in one platform.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
            {[["Tenant Isolation", ShieldCheck], ["Real-time Tracking", Zap], ["Publisher Payouts", Users]].map(([t, I]) => (
              <div key={t} className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
                <I className="mb-2 text-emerald-400" size={18} />
                <p className="text-xs font-medium text-slate-300">{t}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-600">© 2026 TrackingPanel — One Platform. Complete Tracking.</p>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center bg-[var(--tf-bg)] p-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <img src="/tracking-panel-logo.png" alt="TrackingPanel" className="mb-3 h-20 w-auto max-w-[300px] object-contain" />
          </div>
          <h2 className="font-head text-2xl font-bold text-slate-50">Sign in</h2>
          <p className="mt-1 text-sm text-slate-400">Access your tracking workspace.</p>
          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Email</label>
              <input data-testid="login-email" className="tf-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Password</label>
              <input data-testid="login-password" className="tf-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <Btn data-testid="login-submit" type="submit" disabled={loading} className="w-full">
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
            </Btn>
          </form>
          <div className="mt-4"><ForgotPassword /></div>
        </motion.div>
      </div>
    </div>
  );
}
